import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ReactTooltip from 'react-tooltip';

import { RootState } from 'store/reducers';
import { navigateToStep } from 'store/actions/questionnaireActions';
import { getCurrentUser } from 'store/actions/userActions';
import { getRecommendations } from 'store/actions/recommendationsActions';
import { downloadDocument, getAssetsList } from 'store/actions/assetActions';
import { fetchAssistantProgress } from 'store/actions/assistantActions';
import { getNextSteps } from 'store/actions/nextStepActions';

import { Button, Modal } from 'modules/common/components';
import { RegistrationZVRProcess } from 'modules/zvr/components';
import { ProfileForm } from 'modules/profile/components';
import { Link } from 'modules/elements/components';
import { FinalizeStepModal } from 'modules/assistant/components/Questionnaire';

import { QUESTIONNAIRE_KEYS } from 'modules/assistant/constants/questionnaires';
import { STEP_KEYS } from 'modules/assistant/constants/questionnaires/patientDecree';
import { setModal } from 'store/actions/modalActions';
import { MODALS } from 'modules/common/constants';
import { getPotentialContactDeletionStep } from 'modules/assistant/helpers/questionnaireHelper';

export interface QuestionnaireProps {
  disableNextButton: boolean;
  isFirstStep: boolean;
  isEndStep?: boolean;
  prevStepId: STEP_KEYS | null;
  handleSubmission: () => void;
  handleGoToPrevQuestion: any;
  handleResetAllAnswers: () => void;
  questionnaire: QUESTIONNAIRE_KEYS;
  isEndScreenAvailable?: boolean;
  questionnaireData: any;
}

const PrevNextButtons = ({
  disableNextButton,
  isFirstStep,
  isEndStep,
  prevStepId,
  handleSubmission,
  handleGoToPrevQuestion,
  handleResetAllAnswers,
  questionnaire,
  questionnaireData,
  isEndScreenAvailable,
}: QuestionnaireProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['common', 'assistant', questionnaire]);

  const [document, setDocument] = useState({} as DocumentDTO);
  const [loading, setLoading] = React.useState(false);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [isZVRProcess, setIsZVRProcess] = React.useState(false);
  const [isFinalizeStep, setIsFinalizeStep] = React.useState(false);

  const user = useSelector((state: RootState) => state.user.current);
  const contacts: ContactDTO[] =
    useSelector((state: any) => state.contacts.list) || [];

  const isDownloadAllowed = user.verified && user?.profile === 1;
  const isUserEmailVerified = user.verified;

  useEffect(() => {
    dispatch(getAssetsList());
    dispatch(fetchAssistantProgress());
  }, []);

  useEffect(() => {
    if (isEndStep) ReactTooltip.rebuild();
  });

  const handleCloseProfileForm = async () => {
    await dispatch(getCurrentUser());
    setShowProfileForm(false);
  };

  const handleHideModal = () => {
    setLoading(false);
    setIsZVRProcess(false);
    handleCloseProfileForm();
  };

  const handleInitialAssessmentSubmit = async () => {
    await handleSubmission();
    await dispatch(getRecommendations());
    await dispatch(getCurrentUser());
    await dispatch(getNextSteps());
  };

  const handleEnterZVRProcess = (document: DocumentDTO) => {
    setDocument(document);
    setIsZVRProcess(true);
  };

  const handleCancelZVRProcess = () => {
    setIsZVRProcess(false);
  };
  const handleDocumentDownload = async () => {
    setLoading(true);
    await dispatch(downloadDocument(document));
    setLoading(false);
  };

  const handleResetAll = () => {
    dispatch(
      setModal({
        name: MODALS.COMMON_MODAL,
        props: {
          title: t('assistant:reset_all_warning_modal_title'),
          secondaryButtonText: t('assistant:reset_all'),
          primaryButtonText: t('common:cancel'),
          cancelAction: handleResetAllAnswers,
        },
      })
    );
  };

  const handleMissingContacts = (step: string) => {
    dispatch(
      setModal({
        name: MODALS.COMMON_MODAL,
        props: {
          title: t('assistant:contact_deleted_modal_title'),
          primaryButtonText: t('assistant:contact_deleted_modal_button'),
          hasCross: true,
          successAction: () => {
            dispatch(navigateToStep(questionnaire, step));
          },
        },
      })
    );
  };

  const handleFinalizeStep = () => {
    if (!isDownloadAllowed) {
      setShowProfileForm(true);
    } else {
      const missingContactStep = getPotentialContactDeletionStep(
        questionnaire,
        questionnaireData,
        contacts
      );
      if (missingContactStep) {
        handleMissingContacts(missingContactStep);
      } else {
        setIsFinalizeStep(true);
      }
    }
  };

  const handleFinalizeStepCancel = () => {
    setIsFinalizeStep(false);
  };

  const isInitialAssessment =
    questionnaire === QUESTIONNAIRE_KEYS.INITIAL_ASSESSMENT;
  const showPreviousButton =
    !isFirstStep && ((prevStepId && !isEndStep) || isInitialAssessment);

  return (
    <>
      {isZVRProcess && (
        <RegistrationZVRProcess
          downloading={loading}
          onCancelProcess={handleCancelZVRProcess}
          handleDocumentDownload={handleDocumentDownload}
          questionnaire={questionnaire}
        />
      )}

      {showProfileForm && (
        <Modal show onHide={handleHideModal} hasCloseCross>
          <h1 className="Typography Typography--title u-weight-600 t-pb-5">
            {t('assistant:profile_not_complete_form_title')}
          </h1>
          <div className="d-flex">
            <ProfileForm
              isProfilePage={false}
              onClose={handleHideModal}
              onSuccess={handleCloseProfileForm}
            />
          </div>
        </Modal>
      )}

      {isFinalizeStep && (
        <FinalizeStepModal
          questionnaire={questionnaire}
          onClose={handleFinalizeStepCancel}
          handleZVRProcess={handleEnterZVRProcess}
        />
      )}

      <div className="t-flex xs:t-flex-wrap t-justify-end xs:t-mx-5 xl:t-mx-10 t-my-12">
        {showPreviousButton ? (
          <div className="sm:t-mr-auto xs:t-mb-5 sm:t-mb-0">
            <Button category="secondary" onClick={handleGoToPrevQuestion}>
              {t('common:previous')}
            </Button>
          </div>
        ) : isEndStep && !isInitialAssessment ? (
          <div className="sm:t-mr-auto xs:t-mb-5 sm:t-mb-0">
            <Button onClick={handleResetAll} category="secondary">
              {t('assistant:reset_all')}
            </Button>
          </div>
        ) : (
          <Link
            to="/assistant"
            className="button-secondary sm:t-mr-auto xs:t-mb-5 sm:t-mb-0"
          >
            {t('common:back')}
          </Link>
        )}

        {isInitialAssessment && isEndStep && (
          <Button
            onClick={handleInitialAssessmentSubmit}
            disabled={disableNextButton}
            loading={loading}
          >
            {t('common:save_and_continue')}
          </Button>
        )}

        {isEndStep && !isInitialAssessment ? (
          <div
            data-tip={
              isUserEmailVerified ? '' : t('assistant:email_not_verified')
            }
          >
            <Button
              className="t-ml-5"
              onClick={handleFinalizeStep}
              disabled={!isUserEmailVerified}
            >
              {t('assistant:prevNextButtons_finalize_btn')}
            </Button>
          </div>
        ) : (
          !isEndStep && (
            <Button
              onClick={handleSubmission}
              disabled={disableNextButton}
              className="xs:t-mb-5 sm:t-mb-0 xs:t-ml-5"
            >
              {isFirstStep && !isInitialAssessment ? (
                <>
                  <span className="xs:t-hidden xl:t-inline">
                    {t(`${questionnaire}:start_screen_button`)}
                  </span>
                  <span className="xs:t-inline xl:t-hidden">
                    {t('common:start')}
                  </span>
                </>
              ) : (
                <p>{t('common:next')}</p>
              )}
            </Button>
          )
        )}

        {!isEndStep && isEndScreenAvailable && (
          <div className="xs:t-mt-3 sm:t-mt-0 t-self-start">
            <Button
              className="t-ml-5"
              onClick={() => {
                handleSubmission();
                dispatch(navigateToStep(questionnaire, STEP_KEYS.STEP_END));
              }}
              disabled={disableNextButton}
            >
              {t('assistant:go_to_end_screen')}
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default PrevNextButtons;
